import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { IconComponent } from '../common/components/general/icon/icon.component';
import { UserMenuComponent } from './user-menu/user-menu.component';
import { MatMenuModule } from '@angular/material/menu';
import { NotificationsMenuComponent } from './notifications-menu/notifications-menu.component';
import { MenuComponent } from './menu/menu.component';
import { COMPONENT_META_DATA } from '../common/constants/component-meta-data';
import { ContentModel } from '../common/models/content.model';
import { HeaderService } from './header.service';
import { Router } from '@angular/router';
import { ContentFilterPipe } from '../common/pipes/content-filter.pipe';
import { NgIf } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CUSTOM_BREAKPOINTS } from '../common/constants/breakpoints';
import { MatExpansionModule } from '@angular/material/expansion';
import { MenuAppLinks, MenuHeader } from './menu/menu.model';
import { HeaderResponse } from './header.viewmodel';
import { ElementsService } from '../common/services/elements.service';
import { LPMI_ROUTES } from '../common/constants/routes';
import { SharedMenuService } from './shared-menu.service';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogMenuComponent } from './menu/dialog-menu/dialog-menu.component';
import { OverlayContainer } from '@angular/cdk/overlay';
import { GtmEvent } from '../common/models/gtm-event.model';
import { GtmService } from '../common/services/gtm/gtm.service';
import { LanguageResponse, Languages, LoginResults, LoginSelections } from '../login/login.viewmodel';
import { CACHE_KEYS } from '../common/constants/cache-keys';
import { CacheService } from '../common/services/cache.service';
import { addFragmentAndReload, isLPMIHost } from '../common/util/utils';
import { environment } from '../../environments/environment';
import { LanguageSwitcherComponent } from "../common/components/language-switcher/language-switcher.component";
import { LoginService } from '../login/login.service';
import { Language } from '../common/components/language-switcher/language-switcher.model';
import { SelectComponent } from "../common/components/general/select/select.component";
import { FormControl } from '@angular/forms';

@Component({
  selector: 'nuc-header',
  imports: [MatExpansionModule, MatToolbar, IconComponent, UserMenuComponent, MatMenuModule, NotificationsMenuComponent, MenuComponent, NgIf, LanguageSwitcherComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnChanges {
  @Input() customThemeClass: string;
  @Input() pagePath: string;
  LABELS = COMPONENT_META_DATA.HEADER.LABELS;
  componentType: string = COMPONENT_META_DATA.HEADER.VALUE;
  pageContent: ContentModel[];
  isExpandMoreOpen: boolean = false;
  size: number;
  headerData: MenuHeader;
  appLinks: MenuAppLinks;
  pagePathFromOrigin: String;
  displayClass: string = "blueTheme";
  gloablSearchPagePath: string;

  userMenuMobileState: boolean = false;
  notificationsMobileMenu: boolean = false;
  desktopOverlayState: boolean = false;
  menuState: { menu: string, open: boolean } = { menu: '', open: false };
  dialogRef1: MatDialogRef<DialogMenuComponent> | null = null;
  logoBlue: string;
  logoWhite: string;
  businessUnit?: string;
  loginSelections: LoginSelections;
  userType: string;
  loginData?: LoginResults;
  langOptions?: Languages[];
  langValue = new FormControl<Language | null>(null);

  // poc: Flag to enable/disable AEM Universal Editor integration
  poc: boolean = true;

  // poc: AEM Universal Editor integration paths
  private readonly AUE_BASE_PATH = '/content/lpmi';
  private readonly AUE_COMPONENT_PATH = 'header/jcr:content/root/lpmi_container/header';

  // poc: Returns AUE resource URN when poc flag is enabled
  get aueResource(): string | undefined {
    if (this.poc) {
      return this.getAueResource();
    }
    return undefined;
  }

  // poc: Generates dynamic AEM Universal Editor resource URN based on user context
  private getAueResource(): string {
    const loginSelections: LoginSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS);
    const region = loginSelections?.region ?? 'ee';
    const market = loginSelections?.market?.toLowerCase() ?? 'de';
    const language = (this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE) as { languageCode: string })?.languageCode ?? 'en';
    const businessUnit = loginSelections?.isAdmin ? 'admin' : (loginSelections?.selectedBusinessLabel?.key ?? 'service');
    return `urn:aemconnection:${this.AUE_BASE_PATH}/${region}/${market}/${language}/${businessUnit}/${this.AUE_COMPONENT_PATH}`;
  }

  constructor(private headerService: HeaderService, private breakpointObserver: BreakpointObserver, private router: Router, private elementRef: ElementRef, private elementsService: ElementsService, private sharedMenuService: SharedMenuService, private overlayContainer: OverlayContainer, private gtm: GtmService,
    private cacheService: CacheService, private loginService: LoginService
  ) {

  }
  ngOnChanges(changes: SimpleChanges): void {
    this.pagePathFromOrigin = window.location.href;
    this.loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    this.userType = this.loginData?.userType;
    this.displayClass = "landingBlackTheme";
  }

  ngOnInit(): void {
    // this.displayClass = this.displayClass;
    this.initializePage();
    this.elementsService.setAngularContext(this.elementRef);
    this.overlayContainer.getContainerElement().classList.add('nuc-overlay-container');
    // Subscribe to the shared service to listen for menu state changes
    this.sharedMenuService.menuState$.subscribe(state => {
      this.menuState = state;
      if (state.menu === "notifications") {
        this.userMenuMobileState = false; // close userMenu if notifications open
        this.notificationsMobileMenu = state.open; // open notifications menu if notifications open, close if notifications close
      }
      if (state.menu === "profile") {
        this.userMenuMobileState = state.open; // open profile menu if profile open, close if profile close
        this.notificationsMobileMenu = false; // close notifications menu if profile open
      }

      if (!state.menu) {
        this.desktopOverlayState = !this.desktopOverlayState;
      }
    });
  }

  initializePage() {
    this.loginSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS);
    this.businessUnit = this.loginSelections?.selectedBusinessLabel?.key;
    this.displayClass = "landingBlackTheme";
    this.breakpointObserver.observe([CUSTOM_BREAKPOINTS.mobile]).subscribe((result) => {
      if (!result.breakpoints[CUSTOM_BREAKPOINTS.mobile]) {
        this.closeDialog();
        this.size = 32;
      } else {
        this.size = 32;
      }
    });

    const market = this.loginData?.market;
    const region = this.loginData?.region;
    this.loginService.getLanguagesByMarketCode(market, region).subscribe((res: LanguageResponse) => {
      this.langOptions = res?.results?.languages;
      if (res?.results?.languages?.length === 0) {
        let lang: Languages = {
          "languageCode": "en",
          "languageName": "English",
          "languageLabel": "English (United Kingdom)",
          "isDefault": true,
          "isoCode": "en-gb"
        }
        this.langOptions.push(lang);
        let preflanguageCode: Language = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE);
        if (preflanguageCode && this.langOptions?.length) {
          let langCode = this.langOptions?.find((x) => x.languageCode === preflanguageCode?.languageCode);
          if (langCode) {
            this.langValue.setValue(langCode);
          }
        }
      }
      else {
        let preflanguageCode: Language = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE);
        if (preflanguageCode && this.langOptions?.length) {
          let langCode = this.langOptions?.find((x) => x.languageCode === preflanguageCode?.languageCode);
          if (langCode) {
            this.langValue.setValue(langCode);
          }
        }
      }
    });

    this.headerService.getHeader().subscribe((response: HeaderResponse) => {
      this.headerData = response.results?.header;
      this.appLinks = response.results?.appLinks;
      this.pageContent = response.results?.labels;
      this.logoBlue = response.results?.header?.desktopBlueLogo;
      this.logoWhite = response.results?.header?.desktopWhiteLogo;
    });

  }

  onLanguageChange(lang: Languages) {
    const cache_language: Language = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE);
    const isoCode = lang?.isoCode;
    let languageCode = isoCode?.split('-')[0]?.toLowerCase();
    let countryCode = isoCode?.split('-')[1]?.toUpperCase();
    if (lang?.languageCode?.toLowerCase() != cache_language?.languageCode?.toLowerCase()) {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.LANGUAGE, { languageCode, countryCode });
      addFragmentAndReload();
    }
  }

  handleMenuAction(event: string) {
    if (event === 'open') {
      if (isLPMIHost(window.location.hostname, environment.LPMI_URL)) {
        this.displayClass = "landingBlackTheme";
      }
      //this.cdr.detectChanges();
    } else {
      if (isLPMIHost(window.location.hostname, environment.LPMI_URL)) {
        this.displayClass = "landingBlackTheme";
      }
      //this.cdr.detectChanges();
    }
  }

  openExpandMoreDialog(templateRef?: any) {
    this.isExpandMoreOpen = true;
  }

  closeDialog(): void {
    this.isExpandMoreOpen = false;
  }

  redirectToLanding() {
    const gtmEvent = GtmEvent.create("HEADER", 'Header', 'Redirection::External', 'GDRP');
    this.gtm.pushEvent(gtmEvent);
    if (this.elementsService.isEmbeddedInAngularApp) {
      this.router.navigate([`/${LPMI_ROUTES.LANDING.PATH}`]);
    } else {
      window.location.href = `/${LPMI_ROUTES.LANDING.PATH}`;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInside = target.closest('.mobileMenuOptions, .expandMore-icon');
    if (!clickedInside) {
      this.isExpandMoreOpen = false;
    }

    const onheaderToolBarClick = target.closest('.header-toolbar');
    if (onheaderToolBarClick) {
      // Set the state when clicked on header tool bar
      this.sharedMenuService.toggleMenu('', false);
    }
  }

  onClick() {
    const gtmEvent = GtmEvent.create("HEADER", 'Header', 'Search', 'Search');
    this.gtm.pushEvent(gtmEvent);
    this.isExpandMoreOpen = false;
    if (!this.elementsService.isEmbeddedInAngularApp) {
      window.location.href = `/${LPMI_ROUTES.SEARCH.PATH}`;
    } else {
      this.router.navigate([`/${LPMI_ROUTES.SEARCH.PATH}`]);
    }
  }
}
